import React, { useState } from "react";
import {
  Table,
  Button,
  Modal,
  Spinner,
  Card,
  Row,
  Col,
} from "react-bootstrap";
import { useGetCancellationRequests } from "../../api/queries/useGetCancellationRequests";
import { useGetCancellationRequestDetail } from "../../api/queries/useGetCancellationRequestDetail";
import { useApproveCancellationRequest } from "../../api/mutations/useApproveCancellationRequest";
import { useRejectCancellationRequest } from "../../api/mutations/useRejectCancellationRequest";
import { CancellationRequest } from "../../types/Types";
import { CANCELLATION_REASONS } from "../../types/Types";
import { FaEye, FaCheck, FaTimes } from "react-icons/fa";

type ActionModal = "approve" | "reject" | "view" | null;

const Cancellations: React.FC = () => {
  const [actionModal, setActionModal] = useState<ActionModal>(null);
  const [selectedRequest, setSelectedRequest] = useState<CancellationRequest | null>(null);

  const { data: requests = [], isLoading } = useGetCancellationRequests();
  const { data: detailData } = useGetCancellationRequestDetail(
    selectedRequest?.id ?? null,
    actionModal === "view"
  );
  const displayRequest = (actionModal === "view" && detailData) ? detailData : selectedRequest;
  const { mutate: approve, isPending: isApproving } = useApproveCancellationRequest({
    onSuccess: () => {
      setActionModal(null);
      setSelectedRequest(null);
    },
  });
  const { mutate: reject, isPending: isRejecting } = useRejectCancellationRequest({
    onSuccess: () => {
      setActionModal(null);
      setSelectedRequest(null);
    },
  });

  const sortedRequests = [...requests].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const pendingRequests = sortedRequests.filter(
    (r) => (r.status as string) === "Requested"
  );

  const openView = (req: CancellationRequest) => {
    setSelectedRequest(req);
    setActionModal("view");
  };

  const openApprove = (req: CancellationRequest) => {
    setSelectedRequest(req);
    setActionModal("approve");
  };

  const openReject = (req: CancellationRequest) => {
    setSelectedRequest(req);
    setActionModal("reject");
  };

  const handleApprove = () => {
    if (selectedRequest?.id) approve(selectedRequest.id);
  };

  const handleReject = () => {
    if (selectedRequest?.id) reject(selectedRequest.id);
  };

  const reasonLabel = (code: number) =>
    CANCELLATION_REASONS.find((r) => r.id === code)?.label ?? `Reason ${code}`;

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString(undefined, {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="p-2">
      <h5 className="mb-3">Cancellation requests</h5>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Date & time</th>
            <th>Full name</th>
            <th>Business name</th>
            <th>City / Country</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pendingRequests.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center text-muted py-4">
                No pending cancellation requests
              </td>
            </tr>
          ) : (
            pendingRequests.map((req) => (
              <tr key={req.id}>
                <td>{formatDateTime(req.createdAt)}</td>
                <td>{req.fullName ?? "—"}</td>
                <td>{req.businessName ?? "—"}</td>
                <td>
                  {[req.city, req.country].filter(Boolean).join(", ") || "—"}
                </td>
                <td className="text-center">
                  <Button
                    variant="link"
                    className="p-1 text-primary"
                    title="View"
                    onClick={() => openView(req)}
                  >
                    <FaEye size={18} />
                  </Button>
                  <Button
                    variant="link"
                    className="p-1 text-success"
                    title="Approve"
                    onClick={() => openApprove(req)}
                  >
                    <FaCheck size={18} />
                  </Button>
                  <Button
                    variant="link"
                    className="p-1 text-danger"
                    title="Reject"
                    onClick={() => openReject(req)}
                  >
                    <FaTimes size={18} />
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* View detail modal */}
      <Modal show={actionModal === "view"} onHide={() => setActionModal(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Cancellation request details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {displayRequest && (
            <Card className="border-0 bg-light">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Date:</strong> {formatDateTime(displayRequest.createdAt)}
                    </p>
                    <p className="mb-1">
                      <strong>Full name:</strong> {displayRequest.fullName ?? "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Email:</strong> {displayRequest.email ?? "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Phone:</strong> {displayRequest.phoneNumber ?? "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Business name:</strong> {displayRequest.businessName ?? "—"}
                    </p>
                    <p className="mb-1">
                      <strong>City / Country:</strong>{" "}
                      {[displayRequest.city, displayRequest.country].filter(Boolean).join(", ") || "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Membership:</strong> {displayRequest.membershipName ?? "—"}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Business address:</strong>{" "}
                      {[displayRequest.businessAddressLineOne, displayRequest.businessAddressLineTwo]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Post code:</strong> {displayRequest.businessPostCode ?? "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Business telephone:</strong> {displayRequest.businessTelephoneNumber ?? "—"}
                    </p>
                    <p className="mb-1">
                      <strong>User status:</strong> {displayRequest.userStatus ?? "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Reason:</strong> {reasonLabel(displayRequest.reasonCode ?? displayRequest.reasonId ?? 0)}
                    </p>
                    {displayRequest.improvementFeedback && (
                      <p className="mb-1">
                        <strong>Feedback:</strong> {displayRequest.improvementFeedback}
                      </p>
                    )}
                    <p className="mb-1">
                      <strong>Stripe customer ID:</strong> {displayRequest.stripeCustomerId ?? "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Stripe subscription ID:</strong> {displayRequest.stripeSubscriptionId ?? "—"}
                    </p>
                    <p className="mb-1">
                      <strong>Stripe status:</strong> {displayRequest.stripeStatus ?? "—"}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setActionModal(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approve confirmation */}
      <Modal show={actionModal === "approve"} onHide={() => setActionModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Approve cancellation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you would like to approve this cancellation request? This
          user&apos;s membership will be cancelled by performing this action.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setActionModal(null)}>
            No
          </Button>
          <Button variant="danger" onClick={handleApprove} disabled={isApproving}>
            {isApproving ? "Processing..." : "Yes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject confirmation */}
      <Modal show={actionModal === "reject"} onHide={() => setActionModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reject cancellation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to reject this cancellation request? This
          user&apos;s account will be reinstated and NOT cancelled by performing
          this action.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setActionModal(null)}>
            No
          </Button>
          <Button variant="primary" onClick={handleReject} disabled={isRejecting}>
            {isRejecting ? "Processing..." : "Yes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Cancellations;
